import { PageRoutes } from "./routes";
import {
  isSubscriptionDisabled,
  isTizen,
  isWebos,
  type Platform,
} from "../utils/platform";

export const SCROLL_AMOUNT = 200;

export enum SettingsView {
  Settings = "Settings",
  General = "General Settings",
  ProfileInfo = "Account Info",
  LegalInformation = "Legal Information",
  OpenSource = "Open Source Libraries",
  Privacy = "Privacy Policy",
  Tnc = "Terms & Conditions",
  Support = "Support & FAQs",
  Exit = "Exit",
  Subscription = "Subscription",
  StreamingProfile = "Streaming Profile",
}

const settingGroups = [
  [
    {
      icon: "icon-linear-setting-2",
      title: "General Settings",
      settingsView: SettingsView.General,
      pageRoute: PageRoutes.General,
    },
    {
      icon: "icon-linear-user",
      title: "Account Info",
      settingsView: SettingsView.ProfileInfo,
      pageRoute: PageRoutes.ProfileInfo,
    },
    {
      icon: "icon-linear-menu-wallet",
      title: "Subscription",
      settingsView: SettingsView.Subscription,
      pageRoute: PageRoutes.Subscription,
    },
  ],
  [
    {
      icon: "icon-linear-security-safe",
      title: "Legal Information",
      settingsView: SettingsView.LegalInformation,
      pageRoute: PageRoutes.LegalInformation,
    },
  ],
  [
    {
      icon: "icon-linear-messages",
      title: "Support & FAQs",
      settingsView: SettingsView.Support,
      pageRoute: PageRoutes.Support,
    },
  ],
  [
    {
      icon: "icon-logout",
      title: "Exit",
      settingsView: SettingsView.Exit,
      pageRoute: PageRoutes.Exit,
    },
  ],
];

// Builds with the subscription flow disabled must not expose the menu entry.
export const baseSettingGroups = isSubscriptionDisabled
  ? settingGroups.map((group) =>
      group.filter((item) => item.settingsView !== SettingsView.Subscription),
    )
  : settingGroups;

export const REASONS_UNSUBSCRIBE = [
  "Too expensive",
  "Not using enough",
  "Not enough games",
  "Can not get steam to work",
  "Other",
];

export const STORAGE_FULLSCREEN = "isFullscreen";

export const STORAGE_STATS_PINNED = "isStatsPinned";

export const LIMIT_USERNAME = 16;

export const FAQ_LINK =
  "https://play-onmo.helpshift.com/hc/en/5-onmo-1773744629/?han=1&hpn=1&p=web&hbhf=1";

// https://onmoplus.com/data/* (see vite.config.ts) to avoid CORS.
export const TNC_DOC_URL = "https://onmoplus.com/data/terms.html";
export const PRIVACY_DOC_URL = "https://onmoplus.com/data/privacy.html";

export const OPEN_SOURCE_DOC_URL = "https://onmoplus.com/data/open-source.html";

// TV (QR code) legal document links
export const TNC_QR_URL = "https://onmoplus.com/terms-and-conditions";
export const PRIVACY_QR_URL = "https://onmoplus.com/privacy-policy";
export const OPEN_SOURCE_QR_URL = "https://onmoplus.com/open-source";
export const FAQ_QR_URL = "https://onmoplus.com/support";
export const FAQ_LINK_URL = "https://help.onmoplus.com/";

export interface ILegalGroupItem {
  title: string;
  link: string;
  qrUrl: string;
  docUrl: string;
  settingsView?: SettingsView;
  pageRoute?: PageRoutes;
}

export const LEGAL_DETAIL_VIEWS: SettingsView[] = [
  SettingsView.Tnc,
  SettingsView.Privacy,
  SettingsView.OpenSource,
];
export const GENERAL_DETAIL_VIEWS: SettingsView[] = [
  SettingsView.StreamingProfile,
];

export const resolveMenuView = (view: SettingsView): SettingsView => {
  if (LEGAL_DETAIL_VIEWS.includes(view)) return SettingsView.LegalInformation;
  if (GENERAL_DETAIL_VIEWS.includes(view)) return SettingsView.General;
  return view;
};

export const SETTINGS_VIEW_ROUTES: Partial<Record<SettingsView, PageRoutes>> = {
  [SettingsView.General]: PageRoutes.General,
  [SettingsView.ProfileInfo]: PageRoutes.ProfileInfo,
  [SettingsView.Subscription]: PageRoutes.Subscription,
  [SettingsView.LegalInformation]: PageRoutes.LegalInformation,
  [SettingsView.Support]: PageRoutes.Support,
  [SettingsView.Exit]: PageRoutes.Exit,
  [SettingsView.Privacy]: PageRoutes.Privacy,
  [SettingsView.Tnc]: PageRoutes.Tnc,
  [SettingsView.OpenSource]: PageRoutes.OpenSource,
};

export const LEGAL_GROUP: ILegalGroupItem[] = [
  {
    title: "Terms & Conditions",
    link: "onmoplus.com/terms-and-conditions",
    qrUrl: TNC_QR_URL,
    docUrl: TNC_DOC_URL,
    settingsView: SettingsView.Tnc,
    pageRoute: PageRoutes.Tnc,
  },
  {
    title: "Privacy Policy",
    link: "onmoplus.com/privacy-policy",
    qrUrl: PRIVACY_QR_URL,
    docUrl: PRIVACY_DOC_URL,
    settingsView: SettingsView.Privacy,
    pageRoute: PageRoutes.Privacy,
  },
  {
    title: "Open Source Libraries",
    link: "onmoplus.com/open-source",
    qrUrl: OPEN_SOURCE_QR_URL,
    docUrl: OPEN_SOURCE_DOC_URL,
    settingsView: SettingsView.OpenSource,
    pageRoute: PageRoutes.OpenSource,
  },
];

export interface IOpenSourceLibrary {
  slug: string;
  name: string;
  license: string;
  platforms: Platform[];
}

export const OPEN_SOURCE_DESCRIPTION =
  "This app is built with the help of the following open source libraries. Select one to read its license.";

const APP_ROOT_URL = globalThis.location.href
  .split(/[?#]/)[0]
  .replace(/[^/]*$/, "");

export const OPEN_SOURCE_DOC_BASE =
  isWebos || isTizen
    ? `${APP_ROOT_URL}legal/open-source/`
    : `/legal/open-source/`;

export const OPEN_SOURCE_INDEX_URL = `${OPEN_SOURCE_DOC_BASE}index.json`;
export const getOpenSourceDocUrl = (slug: string) =>
  `${OPEN_SOURCE_DOC_BASE}${slug}.html`;
